import React, { useEffect, useRef, useState } from 'react';
import Chart, { Plugin } from 'chart.js/auto';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { ScaleOptions, DataSet, Tooltip } from './types';
import { summaryLabelsPlugin } from './plugins';
import { calculateSpaceByCharacter, getMainGraphWidth } from '../../common/util';
import { CHANGE_TYPE_AND_LABEL_POSITION_MAP } from './stackedBarChart';
import { ContextType, renderTooltip } from './tooltip';
import { getChartHeight } from './utils';

export type SlopeData = {
  labels: (string[] | string)[];
  sets: DataSet[];
  summary?: DataSet;
  additionalSummary?: DataSet;
};

export type SlopeScaleOptions = ScaleOptions & {
  useMax?: number;
};

export type SlopeChartOptions = {
  xScale?: ScaleOptions;
  yScale?: SlopeScaleOptions;
  data?: SlopeData;
  tooltip?: Tooltip;
};

type Props = {
  options: SlopeChartOptions;
  trackTooltipEvent: () => void;
  isMobile: boolean;
  isChartFullView: boolean;
  chartHeightOffset?: number;
};
function SlopeChart({ options, trackTooltipEvent, isMobile, isChartFullView, chartHeightOffset = 0 }: Readonly<Props>) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const optionsRef = useRef<string>(null);
  const [fullChartWidth, setFullChartWidth] = useState<number>(240);

  const {
    xScale,
    yScale,
    data: { labels, sets, summary, additionalSummary },
    tooltip,
  } = options;

  const tooltipFormat = item => {
    const formattedValue = tooltip?.valueFormatter ? tooltip.valueFormatter(Number(item.raw)) : item.formattedValue;
    return `${item.dataset.label}: ${formattedValue}`;
  };

  useEffect(() => {
    const { data } = options;

    if (!canvasRef.current || !data || optionsRef.current === JSON.stringify(options)) {
      return;
    }

    optionsRef.current = JSON.stringify(options);

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const plugins: Plugin[] = [ChartDataLabels];
    if (summary) {
      plugins.push(summaryLabelsPlugin(summary, CHANGE_TYPE_AND_LABEL_POSITION_MAP[summary.changeType]));
    }
    if (additionalSummary) {
      const summaryLabel = summaryLabelsPlugin(
        additionalSummary,
        CHANGE_TYPE_AND_LABEL_POSITION_MAP[additionalSummary.changeType]
      );
      plugins.push(summaryLabel);
    }
    let hoverTimeout;

    /**
     * Calculate the maximum space needed for the labels
     * on the left and right side of the graph
     * @param data - the chart's data
     * @returns the width needed for the labels on the left and right side of the graph
     */
    const getSideLabelWidth = (data: SlopeData) => {
      const labels = data.sets.flatMap(set => set.values.formatted);
      const labelsWidth = labels.map(label => calculateSpaceByCharacter(label));

      return Math.max(...labelsWidth);
    };

    const datalabelsOffset = 8;
    const sideLabelWidth = getSideLabelWidth(data);
    const leftLabelWidth = sideLabelWidth + datalabelsOffset;
    const rightLabelWidth = sideLabelWidth + 2 * datalabelsOffset;
    const fullwidth = getMainGraphWidth(data) + leftLabelWidth + rightLabelWidth;

    //In case of no comparison there will be only one vertical axix so width can be divided by half
    const noComparisonPeriod = labels.length === 1;
    const updatedFullWidth = noComparisonPeriod ? fullwidth / 2 : fullwidth;
    setFullChartWidth(updatedFullWidth);

    chartRef.current = new Chart(canvasRef.current, {
      plugins: plugins,
      type: 'line',
      data: {
        labels,
        datasets: sets.map(set => ({
          label: set.label,
          data: set.values.raw,
          borderColor: set.values.fillColor,
          backgroundColor: set.values.fillColor,
          datalabels: {
            // color: set.values.textColor,
            formatter: (_: unknown, ctx: Context) => set.values.formatted[ctx.dataIndex],
            offset: datalabelsOffset,
            anchor: 'center',
            align: (ctx: Context) => (ctx.dataIndex === 0 ? 'left' : 'right'),
            display: 'auto',
          },
          pointStyle: 'rect',
          pointRadius: 5,
        })),
      },
      options: {
        onHover: (_event, _activeElements, chart) => {
          clearTimeout(hoverTimeout);
          hoverTimeout = setTimeout(() => {
            const activeElements = chart.getActiveElements();
            if (activeElements.length > 0) {
              trackTooltipEvent();
            }
          }, 1000);
        },
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: !summary ? 0 : 65,
            left: leftLabelWidth,
            right: noComparisonPeriod ? 0 : rightLabelWidth,
          },
        },
        plugins: {
          tooltip: {
            enabled: false, // Disable default tooltip rendering of slopechart
            external: function (sContext: ContextType) {
              //render custom tooltip for slopechart

              renderTooltip(sContext);
            },
            callbacks: {
              label: tooltipFormat,
            },
          },
          legend: {
            display: false,
          },
        },
        interaction: {
          mode: 'nearest',
          intersect: false,
        },
        scales: {
          x: {
            border: {
              display: false,
            },
            grid: {
              display: true,
            },
            title: {
              display: xScale?.title !== undefined,
              text: xScale?.title || '',
            },
            ticks: {
              callback: function (value: string | number, index: number) {
                if (xScale?.formatter) {
                  return xScale.formatter(value);
                } else {
                  return labels[index];
                }
              },
              maxRotation: 0,
            },
          },
          y: {
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            title: {
              display: false,
            },
            beginAtZero: true,
            ticks: {
              display: false,
            },
            grace: 0,
            min: 0,
            max: yScale?.useMax,
            offset: yScale?.useMax !== undefined,
          },
        },
      },
    });
    return () => {
      clearTimeout(hoverTimeout);
    };
  }, [options]);

  return (
    <div
      data-test-id="slope-chart"
      style={{
        width: fullChartWidth,
        height: getChartHeight(isMobile, isChartFullView, false, chartHeightOffset),
      }}
    >
      <canvas className="h-full w-full" ref={canvasRef} />
    </div>
  );
}

export { SlopeChart };
